/* eslint-disable react/no-unused-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ApolloFragment } from 'apollo-fragment-react';
// graphQL
import gql from 'graphql-tag';
// our components
import { Mutation, Query } from 'components/common/Query';
import WithPermission from 'components/User/WithPermission';
import { adopt } from 'react-adopt';
import { exchangeRateCurrencies } from 'config';
import UserInfo from './Info';

/**
 * Query to fetch user from api
 */
export const USER_INFO_FRAGMENT = `
  fragment fragmentUserInfo on AdminUserInterface {
    id
    publicId
    fakeId
    email
    fullname
    isActive
    rank
    userType
    phone
    lastLogin
    joined
    revolicoJoinedOn
    credit
    rate
    reviewCount
    source
    cardProvider
    photo {
      gcsKey
      url
    }
    location {
          province {
            name
          }
          municipality {
            name
        }
    }
    allowedIps
    paymentLink
    ... on AgencyType {
      commissionLead
      commissionLeadRevolico
      paymentMethod
    }
    ... on UserAgencyType {
      lead
      leadAgency {
        id
        fullname
      }
    }
    __typename
  }
`;

/**
 * Mutation to change user credit
 */
export const CREATE_CREDIT_TRANSACTION_MUTATION = gql`
  mutation(
    $type: AdminCreditTransactionFormType!
    $amount: Float!
    $user: ID!
  ) {
    createCreditTransaction(
      input: { type: $type, amount: $amount, user: $user }
    ) {
      adminCreditTransaction {
        id
        user {
          ...fragmentUserInfo
        }
      }
      errors {
        field
        messages
      }
    }
  }
  ${USER_INFO_FRAGMENT}
`;

/**
 * Query to fetch an exchange rate
 */
export const EXCHANGE_RATE_QUERY = gql`
  query ExchangeRate(
    $currencyFrom: ExchangeRateCurrency!
    $currencyTo: ExchangeRateCurrency!
  ) {
    currentExchangeRates(currencyFrom: $currencyFrom, currencyTo: $currencyTo) {
      rate
    }
  }
`;

/**
 * @class
 * @augments Component
 *
 * @description
 * Stateless component to handle user filters
 *
 * @param {Object} props - Component props.
 *
 * @returns {*} Presentational component.
 */
class UserInfoContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.Compose = adopt({
      mutationCreateCreditTransaction: this.CreateCreditTransaction,
      queryExchangeRate: this.ExchangeRates,
    });
  }

  /**
   * @description
   * Mutation callback when add user is completed.
   *
   * @param {Object} createCreditTransaction - Mutation result.
   * @returns {Function} Callback function to execute in complete.
   */
  createCreditTransactionCompleted = async ({ createCreditTransaction }) => {
    const { errors } = createCreditTransaction;
    if (errors) {
      // eslint-disable-next-line no-console
      console.log(errors);
    }
  };

  /**
   * @description
   * ExchangeRate query callback to compose with mutation.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  ExchangeRates = ({ render }) => (
    <WithPermission
      allowPermissions={['view_current_exchangerate']}
      alt={render({
        exchangeRate: null,
        loadingExchangeRate: false,
      })}
    >
      <Query
        query={EXCHANGE_RATE_QUERY}
        variables={{
          currencyFrom: exchangeRateCurrencies.USD,
          currencyTo: exchangeRateCurrencies.CUP,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="network-only"
      >
        {({ data, loading }) =>
          render({
            exchangeRate: data.currentExchangeRates,
            loadingExchangeRate: loading,
          })
        }
      </Query>
    </WithPermission>
  );

  CreateCreditTransaction = ({ render }) => (
    <Mutation
      mutation={CREATE_CREDIT_TRANSACTION_MUTATION}
      onCompleted={this.createCreditTransactionCompleted}
    >
      {(createCreditTransaction, { loading }) =>
        render({
          createCreditTransaction,
          loadingCreateCreditTransaction: loading,
        })
      }
    </Mutation>
  );

  /**
   * @description
   * Render UserInfo component.
   *
   * @returns {*} Query component.
   */
  render() {
    const { id, data } = this.props;
    return (
      <this.Compose>
        {({
          mutationCreateCreditTransaction: { createCreditTransaction },
          queryExchangeRate: { exchangeRate },
        }) => (
          <ApolloFragment fragment={USER_INFO_FRAGMENT} id={id}>
            {() => (
              <UserInfo
                data={{ ...data, exchangeRate }}
                changeCredit={values =>
                  createCreditTransaction({
                    variables: {
                      user: id,
                      ...values,
                    },
                  })
                }
              />
            )}
          </ApolloFragment>
        )}
      </this.Compose>
    );
  }
}

/**
 * UserInfoContainer PropTypes
 * @param id - prop to know id of the User
 */

UserInfoContainer.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({}).isRequired,
};

export default UserInfoContainer;
